import { startCase, camelCase } from "lodash";
import { FieldDef, IssueDetail } from "models/issue";
import { titleize } from "helpers/string";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { displayRoleName, toRoleFieldName } from "features/Issues/helpers";
import { ResolutionInfo } from "models/resolutions";
import GreenStatusButton from "components/Status/GreenStatusButton";
import { FiCheckCircle } from "react-icons/fi";
import { useMemo } from "react";
import { UserUpdateFormValues } from "components/Forms/UserUpdateForm";
import { Flex } from "@chakra-ui/react";
import UserFormFields from "features/Issues/Components/UserFormFields";
import { NewHireJourney } from "models/v2/newHireJourney";

export function unassignedRoleIssueType(issue: IssueDetail) {
  return `Unassigned ${startCase(camelCase(issue?.context?.roleName || "Role"))}`;
}

export function unassignedRoleValue(issueDetail: IssueDetail) {
  const roleFieldName = toRoleFieldName(issueDetail?.context?.roleName) as keyof NewHireJourney;
  return issueDetail?.newHireJourney ? issueDetail?.newHireJourney[roleFieldName] as string : null;
}

export function useUnassignedRoleValue({ issue }: { issue: IssueDetail }): string | null {
  return unassignedRoleValue(issue);
}

export function IssueModalInputFields({ issue }: { issue: IssueDetail }) {
  const value = useUnassignedRoleValue({ issue });
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(issue?.newHireJourneyId, {
    enabled: !!issue?.newHireJourneyId,
    staleTime: 0,
  });
  const userId = newHireJourney?.newHireId;
  const roleFieldName = toRoleFieldName(issue?.context?.roleName) as keyof UserUpdateFormValues;
  const fields: FieldDef<UserUpdateFormValues>[] = useMemo(() => (
    roleFieldName ? [{ name: roleFieldName, label: `Assign ${issue?.context?.roleName ? titleize(issue?.context?.roleName) : "Role"}` }] : []
  ), [roleFieldName, issue?.context?.roleName]);
  return (
    <Flex direction="column" gap="1">
      {userId && <UserFormFields userId={userId} fieldsDefs={fields} issue={issue} />}
      {value && <GreenStatusButton icon={FiCheckCircle}>{`${displayRoleName(issue)} Assigned`}</GreenStatusButton>}
    </Flex>
  );
}

export function unassignedRoleResolution(issue: IssueDetail): ResolutionInfo {
  const { newHireJourney } = issue || {};
  const roleName = displayRoleName(issue);
  return ({
    issueType: "unassigned_role",
    tagText: unassignedRoleIssueType(issue),
    title: newHireJourney ? `No '${roleName}' assigned for ${newHireJourney?.newHire?.fullName}` : "",
    additionalContext: `Some actions either mention the ${roleName}, or will be sent to the ${roleName}, but no one has been assigned to it. Please assign a ${roleName}.`,
    inputComponent: IssueModalInputFields,
  });
}
